import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';

// Types
interface Admin {
  id: number;
  name: string;
  username: string;
  email: string;
  phone_number?: string;
  is_active: boolean;
}

interface AuthContextType {
  admin: Admin | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  forgotPassword: (username: string) => Promise<boolean>;
  resetPassword: (token: string, newPassword: string) => Promise<boolean>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<boolean>;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// API base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Configure axios defaults
axios.defaults.baseURL = API_BASE_URL;

// Auth Provider Component
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check if user is authenticated
  const isAuthenticated = !!admin;

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      console.log('🔄 Initializing authentication...');
      console.log('📍 Current URL:', window.location.pathname);
      const token = localStorage.getItem('admin_token');
      console.log('🎫 Token found:', !!token);
      
      if (token) {
        try {
          console.log('🔐 Token found, fetching admin profile...');
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          const response = await axios.get('/admin/auth/profile');
          
          if (response.data.success) {
            setAdmin(response.data.data);
            console.log('✅ Admin profile loaded successfully:', response.data.data.name);
          } else {
            console.log('❌ Profile fetch failed, clearing token');
            localStorage.removeItem('admin_token');
            delete axios.defaults.headers.common['Authorization'];
            setAdmin(null);
          }
        } catch (error) {
          console.error('❌ Auth initialization error:', error);
          localStorage.removeItem('admin_token');
          delete axios.defaults.headers.common['Authorization'];
          setAdmin(null);
        }
      } else {
        console.log('❌ No token found, user not authenticated');
        setAdmin(null);
      }
      
      console.log('🏁 Authentication initialization complete');
      setIsLoading(false);
    };

    initAuth();
  }, []);

  // Login function
  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      const response = await axios.post('/admin/auth/login', {
        username,
        password
      });

      if (response.data.success) {
        const { admin: adminData, token } = response.data.data;
        localStorage.setItem('admin_token', token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        setAdmin(adminData);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem('admin_token');
    delete axios.defaults.headers.common['Authorization'];
    setAdmin(null);
  };

  // Forgot password function
  const forgotPassword = async (username: string): Promise<boolean> => {
    try {
      const response = await axios.post('/admin/auth/forgot-password', {
        username
      });
      return response.data.success;
    } catch (error) {
      console.error('Forgot password error:', error);
      return false;
    }
  };

  // Reset password function
  const resetPassword = async (token: string, newPassword: string): Promise<boolean> => {
    try {
      const response = await axios.post('/admin/auth/reset-password', {
        token,
        newPassword
      });
      return response.data.success;
    } catch (error) {
      console.error('Reset password error:', error);
      return false;
    }
  };

  // Change password function
  const changePassword = async (currentPassword: string, newPassword: string): Promise<boolean> => {
    try {
      const response = await axios.post('/admin/auth/change-password', {
        currentPassword,
        newPassword
      });
      return response.data.success;
    } catch (error: any) {
      console.error('Change password error:', error);
      if (error.response?.data?.error_type === 'invalid_password') {
        throw new Error('Current password is incorrect');
      }
      return false;
    }
  };

  const value: AuthContextType = {
    admin,
    isAuthenticated,
    isLoading,
    login,
    logout,
    forgotPassword,
    resetPassword,
    changePassword
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
